/**
 * Plan 10 — Account co-ownership client API surface.
 *
 * Wraps the four REST routes mounted under
 * `/api/family/accounts/:accountId/...` in TanStack Query hooks.
 * Consumer pages (the `/my-family` card, the school-settings sheet,
 * and the equivalent kitchen-settings sheet) import the hooks; they
 * never touch `fetch` directly.
 *
 * Optimistic updates are deliberately omitted on remove + transfer:
 * the rollback semantics under shared-wallet settlement are
 * non-trivial, and the operations are infrequent enough that a brief
 * spinner trumps a flicker-correct optimistic write.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { UserAvatarColor } from '@/lib/user-avatar-colors'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — mirror the REST response shapes server-side
// ---------------------------------------------------------------------------

export interface CoOwnerUser {
  id: string
  firstName: string
  lastName: string
  preferredName: string | null
  avatarUri: string | null
  avatarColor: UserAvatarColor
  email: string | null
  canSignIn: boolean
}

/** The two household guardian tiers, in picker order. */
export type HouseholdRole = 'PRIMARY_GUARDIAN' | 'GUARDIAN'

/** Capability keys — mirrors HOUSEHOLD_PERMISSIONS in @ordo/schema. */
export type HouseholdPermission =
  | 'SEE_BALANCE'
  | 'PLACE_ORDERS'
  | 'MANAGE_STUDENTS'
  | 'TOP_UP'
  | 'MANAGE_PAYMENT_METHODS'
  | 'MANAGE_MEMBERS'
  | 'MANAGE_PRIMARY_GUARDIAN'

export interface CoOwnerRow {
  /**
   * The adult's live PARENT membership on this account, or null for an
   * access-only adult (their membership sits on another household in the same
   * school) or a row-less owner. Key rows on `userId`, not this.
   */
  membershipId: string | null
  userId: string
  user: CoOwnerUser
  addedAt: string
  addedByUserId: string | null
  isOwner: boolean
  /** Null on ORGANIZATION accounts, which don't use household roles. */
  role: HouseholdRole | null
  /** What `role` grants — rendered by the Edit member sheet. */
  permissions: HouseholdPermission[]
}

/** A not-yet-claimed invite, listed alongside co-owners. Dates are ISO strings. */
export interface PendingInviteRow {
  id: string
  email: string
  status: string
  createdAt: string
  expiresAt: string
  lastAttemptAt: string | null
  lastError: string | null
  isExpired: boolean
}

export interface ListCoOwnersResponse {
  coOwners: CoOwnerRow[]
  pendingInvites: PendingInviteRow[]
  /** The signed-in viewer's own role + grants on this account. */
  viewer: {
    userId: string
    role: HouseholdRole | null
    permissions: HouseholdPermission[]
    isOrdoAdmin: boolean
  }
  /**
   * Role -> permissions, served from the shared matrix. The role picker renders
   * its bullets from this so the UI can never claim a grant the server won't
   * enforce.
   */
  roleCatalog: Array<{ role: HouseholdRole; permissions: HouseholdPermission[] }>
}

export interface InviteCoOwnerInput {
  email: string
  name: string
  phone: string
  /** Household role granted on acceptance. Omitted = CO_PARENT. */
  role?: HouseholdRole
}

export interface InvitationSummary {
  id: string
  email: string
  status: string
  accountId: string | null
  expiresAt: string
  createdAt: string
}

export interface InviteCoOwnerResponse {
  invitation: InvitationSummary
}

export interface UpdateCoOwnerInput {
  userId: string
  avatarColor?: UserAvatarColor
  role?: HouseholdRole
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchAccountCoOwners(accountId: string, signal?: AbortSignal): Promise<ListCoOwnersResponse> {
  return apiFetch<ListCoOwnersResponse>(`/api/family/accounts/${encodeURIComponent(accountId)}/co-owners`, { signal })
}

export async function inviteAccountCoOwner(
  accountId: string,
  input: InviteCoOwnerInput,
): Promise<InviteCoOwnerResponse> {
  return apiFetch<InviteCoOwnerResponse>(`/api/family/accounts/${encodeURIComponent(accountId)}/co-owners/invite`, {
    method: 'POST',
    body: input,
  })
}

export async function resendAccountCoOwnerInvite(
  accountId: string,
  invitationId: string,
): Promise<InviteCoOwnerResponse> {
  return apiFetch<InviteCoOwnerResponse>(
    `/api/family/accounts/${encodeURIComponent(accountId)}/co-owners/invites/${encodeURIComponent(invitationId)}/resend`,
    { method: 'POST' },
  )
}

export async function cancelAccountCoOwnerInvite(
  accountId: string,
  invitationId: string,
): Promise<{ cancelledInvitationId: string }> {
  return apiFetch<{ cancelledInvitationId: string }>(
    `/api/family/accounts/${encodeURIComponent(accountId)}/co-owners/invites/${encodeURIComponent(invitationId)}`,
    { method: 'DELETE' },
  )
}

export async function removeAccountCoOwner(
  accountId: string,
  userId: string,
): Promise<{ removedMembershipId: string | null; removedAccessRowCount: number }> {
  return apiFetch<{ removedMembershipId: string | null; removedAccessRowCount: number }>(
    `/api/family/accounts/${encodeURIComponent(accountId)}/co-owners/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  )
}

export async function updateAccountCoOwner(
  accountId: string,
  input: UpdateCoOwnerInput,
): Promise<{ ok: true; role: HouseholdRole | null; user: { id: string; avatarColor: UserAvatarColor } }> {
  const { userId, ...body } = input
  return apiFetch<{ ok: true; role: HouseholdRole | null; user: { id: string; avatarColor: UserAvatarColor } }>(
    `/api/family/accounts/${encodeURIComponent(accountId)}/co-owners/${encodeURIComponent(userId)}`,
    { method: 'PATCH', body },
  )
}

// ---------------------------------------------------------------------------
// TanStack Query hooks
// ---------------------------------------------------------------------------

export const accountCoOwnersQueryKey = (accountId: string) => ['family', 'accounts', accountId, 'co-owners'] as const

export function useAccountCoOwnersQuery(accountId: string | null | undefined) {
  return useQuery<ListCoOwnersResponse>({
    queryKey: accountCoOwnersQueryKey(accountId ?? ''),
    queryFn: ({ signal }) => fetchAccountCoOwners(accountId!, signal),
    enabled: Boolean(accountId),
    // Co-owners change infrequently. 30s is the page-list default;
    // mutations invalidate the key on success, so a stale read between
    // invalidation + refetch is acceptable.
    staleTime: 30_000,
  })
}

export function useInviteAccountCoOwnerMutation(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: InviteCoOwnerInput) => inviteAccountCoOwner(accountId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountCoOwnersQueryKey(accountId) })
      // The /family co-owner surface also affects the family/me owner
      // + members shape. Invalidate it too so the page refreshes
      // alongside the standalone co-owners read.
      void qc.invalidateQueries({ queryKey: ['family', 'me'] })
    },
  })
}

export function useResendCoOwnerInviteMutation(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) => resendAccountCoOwnerInvite(accountId, invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountCoOwnersQueryKey(accountId) })
      void qc.invalidateQueries({ queryKey: ['family', 'me'] })
    },
  })
}

export function useCancelCoOwnerInviteMutation(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) => cancelAccountCoOwnerInvite(accountId, invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountCoOwnersQueryKey(accountId) })
      void qc.invalidateQueries({ queryKey: ['family', 'me'] })
    },
  })
}

export function useRemoveAccountCoOwnerMutation(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => removeAccountCoOwner(accountId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountCoOwnersQueryKey(accountId) })
      void qc.invalidateQueries({ queryKey: ['family', 'me'] })
    },
  })
}

export function useUpdateAccountCoOwnerMutation(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateCoOwnerInput) => updateAccountCoOwner(accountId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountCoOwnersQueryKey(accountId) })
      void qc.invalidateQueries({ queryKey: ['family', 'me'] })
      // A role change moves TOP_UP / MANAGE_PAYMENT_METHODS, which the billing
      // page gates its actions on.
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
    },
  })
}
